import React, { useState, useEffect, useCallback } from 'react';
import { useAccountType } from '@/hooks/useAccountType';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { 
  Users, 
  Plus, 
  Minus, 
  UserCheck, 
  UserX, 
  Mail, 
  Settings,
  Crown,
  Shield,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { teamManagementService, TeamMember, TeamInvitation } from '@/services/teamManagementService';

export const SeatManagement: React.FC = () => {
  const { accountType, permissions, canAddAdditionalSeats } = useAccountType();
  const { toast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<TeamMember['role']>('trader');
  const [isLoading, setIsLoading] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [additionalSeats, setAdditionalSeats] = useState(0);
  const [canManage, setCanManage] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

  // Only load data once when component mounts and user has proper permissions
  useEffect(() => {
    if (canAddAdditionalSeats() && !hasLoadedInitialData) {
      setHasLoadedInitialData(true);
      loadTeamData();
      checkManagePermissions();
    }
  }, [canAddAdditionalSeats, hasLoadedInitialData]);

  const loadTeamData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Reduced timeout for better UX
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 3000)
      );
      
      const dataPromise = Promise.all([
        teamManagementService.getTeamMembers(),
        teamManagementService.getPendingInvitations()
      ]);
      
      const [membersResult, invitationsResult] = await Promise.race([
        dataPromise,
        timeoutPromise
      ]) as any;

      if (membersResult.success) {
        setTeamMembers(membersResult.data || []);
      } else {
        console.error('Failed to load team members:', membersResult.error);
        setTeamMembers([]);
      }

      if (invitationsResult.success) {
        setPendingInvitations(invitationsResult.data || []);
      } else {
        setPendingInvitations([]);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      // Set empty state instead of showing error in development
      setTeamMembers([]);
      setPendingInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkManagePermissions = useCallback(async () => {
    try {
      // Reduced timeout for permissions check
      const timeoutPromise = new Promise<boolean>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 2000)
      );
      
      const canManageTeam = await Promise.race([
        teamManagementService.canManageTeam(),
        timeoutPromise
      ]);
      
      setCanManage(canManageTeam);
    } catch (error) {
      console.error('Error checking manage permissions:', error);
      // Default to true for development/demo purposes
      setCanManage(true);
    }
  }, []);

  const inviteTeamMember = async () => {
    if (!newMemberEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newMemberEmail)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      });
      return;
    }

    const totalSeats = (permissions?.seats || 0) + additionalSeats;
    const usedSeats = teamMembers.filter(m => m.status === 'active').length;
    const pendingSeats = pendingInvitations.length;

    if (usedSeats + pendingSeats >= totalSeats) {
      toast({
        title: 'Seat Limit Reached',
        description: `You've reached your seat limit of ${totalSeats}. Purchase additional seats or remove a team member.`,
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsInviting(true);

      // Reduced timeout for invite operation
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 8000)
      );
      
      const result = await Promise.race([
        teamManagementService.inviteTeamMember({
          inviteeEmail: newMemberEmail,
          role: newMemberRole
        }),
        timeoutPromise
      ]) as any;

      if (result.success) {
        setNewMemberEmail('');
        toast({
          title: 'Invitation Sent',
          description: `Invitation sent to ${newMemberEmail}`,
        });
        
        // Only reload data if not in development mode to avoid extra calls
        const isDevelopment = window.location.hostname === 'localhost';
        if (!isDevelopment) {
          await loadTeamData();
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to send invitation',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error inviting team member:', error);
      
      if (error.message === 'Request timeout') {
        toast({
          title: 'Request Timeout',
          description: 'The invitation request timed out. Please try again later.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to send invitation',
          variant: 'destructive'
        });
      }
    } finally {
      setIsInviting(false);
    }
  };

  const removeTeamMember = async (memberId: string) => {
    try {
      setIsLoading(true);
      
      const result = await teamManagementService.removeTeamMember(memberId);
      
      if (result.success) {
        toast({
          title: 'Member Removed',
          description: 'Team member has been removed',
        });
        
        // Reload team data to reflect changes
        await loadTeamData();
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to remove team member',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove team member',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const purchaseAdditionalSeats = (count: number) => {
    // Simulate seat purchase - in real implementation, this would integrate with billing
    setAdditionalSeats(prev => Math.max(0, prev + count));
    
    toast({
      title: 'Seats Updated',
      description: `${count > 0 ? 'Added' : 'Removed'} ${Math.abs(count)} seat(s). Changes will be reflected in your next billing cycle.`,
    });
  };

  const getRoleIcon = (role: TeamMember['role']) => {
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-yellow-400" />;
      case 'trader': return <Shield className="w-4 h-4 text-blue-400" />;
      case 'analyst': return <Settings className="w-4 h-4 text-green-400" />;
      default: return <Users className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status: TeamMember['status']) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-900/30 border-green-600 text-green-400"><UserCheck className="w-3 h-3 mr-1" />Active</Badge>;
      case 'suspended':
        return <Badge variant="outline" className="border-red-400 text-red-400"><UserX className="w-3 h-3 mr-1" />Suspended</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="border-gray-400 text-gray-400"><UserX className="w-3 h-3 mr-1" />Inactive</Badge>;
      default:
        return <Badge className="bg-green-900/30 border-green-600 text-green-400"><UserCheck className="w-3 h-3 mr-1" />Active</Badge>;
    }
  };

  if (!canAddAdditionalSeats()) {
    return (
      <Card className="border-amber-600/50 bg-amber-900/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Users className="w-5 h-5" />
            Seat Management
            <Badge variant="outline" className="border-amber-400 text-amber-400">Upgrade Required</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-4">
            Seat management is available for Quant Pro and Enterprise accounts. 
            Upgrade to invite team members and manage access.
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700 text-slate-50">
            Upgrade to Quant Pro
          </Button>
        </CardContent>
      </Card>
    );
  }

  const totalSeats = (permissions?.seats || 0) + additionalSeats;
  const usedSeats = teamMembers.filter(m => m.status === 'active').length;
  const pendingSeats = pendingInvitations.length;
  const availableSeats = totalSeats - usedSeats - pendingSeats;

  return (
    <div className="space-y-6">
      {/* Seat Overview */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Users className="w-5 h-5" />
            Seat Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-900/30 rounded-lg border border-blue-800/50">
              <div className="text-2xl font-bold text-blue-400">{totalSeats}</div>
              <div className="text-sm text-slate-400">Total Seats</div>
            </div>
            <div className="text-center p-4 bg-green-900/30 rounded-lg border border-green-800/50">
              <div className="text-2xl font-bold text-green-400">{usedSeats}</div>
              <div className="text-sm text-slate-400">Active Members</div>
            </div>
            <div className="text-center p-4 bg-yellow-900/30 rounded-lg border border-yellow-800/50">
              <div className="text-2xl font-bold text-yellow-400">{pendingSeats}</div>
              <div className="text-sm text-slate-400">Pending Invites</div>
            </div>
            <div className="text-center p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
              <div className="text-2xl font-bold text-slate-300">{availableSeats}</div>
              <div className="text-sm text-slate-400">Available Seats</div>
            </div>
          </div>

          {/* Purchase Additional Seats */}
          <div className="flex items-center gap-2 p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-200 mb-1">Purchase Additional Seats</div>
              <div className="text-xs text-slate-400">Buy more seats to invite additional team members</div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => purchaseAdditionalSeats(-1)}
                disabled={additionalSeats <= 0}
                className="border-slate-600 bg-slate-600 hover:bg-slate-500 text-slate-200"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 bg-slate-600 border border-slate-500 rounded text-sm min-w-[3rem] text-center text-slate-200">
                {additionalSeats}
              </span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => purchaseAdditionalSeats(1)}
                className="border-slate-600 bg-slate-600 hover:bg-slate-500 text-slate-200"
              >
                <Plus className="w-4 h-4" />
              </Button>
              <span className="text-xs text-slate-500 ml-2">$50/seat/month</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite New Member */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100">Invite Team Member</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="email" className="text-slate-300">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
                className="bg-slate-600 border-slate-500 text-slate-50 placeholder-slate-400"
              />
            </div>
            <div>
              <Label htmlFor="role" className="text-slate-300">Role</Label>
              <select 
                id="role"
                className="w-full px-3 py-2 border border-slate-500 rounded-md bg-slate-600 text-slate-50"
                value={newMemberRole}
                onChange={(e) => setNewMemberRole(e.target.value as TeamMember['role'])}
              >
                <option value="trader">Trader</option>
                <option value="analyst">Analyst</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          </div>
          <Button 
            onClick={inviteTeamMember}
            disabled={isInviting || availableSeats <= 0 || !canManage}
            className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-slate-50"
          >
            {isInviting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Send Invitation
              </>
            )}
          </Button>
          {availableSeats <= 0 && (
            <p className="text-sm text-amber-400">
              No available seats. Purchase additional seats to invite more members.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-100 flex items-center gap-2">
              <Mail className="w-5 h-5 text-yellow-400" />
              Pending Invitations ({pendingInvitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-3 border border-yellow-800/50 rounded-lg bg-yellow-900/10">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-yellow-400" />
                    <div>
                      <div className="font-medium text-slate-100">{invitation.invitee_email}</div>
                      <div className="text-sm text-slate-400 capitalize">{invitation.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-yellow-400 text-yellow-400">
                      <Mail className="w-3 h-3 mr-1" />
                      Pending
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => teamManagementService.cancelInvitation(invitation.id)}
                      disabled={isLoading}
                      className="border-slate-600 bg-slate-600 hover:bg-slate-500 text-slate-200"
                      title="Cancel invitation"
                    >
                      <UserX className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members List */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100">Active Team Members</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border border-slate-600 rounded-lg bg-slate-700/50">
                <div className="flex items-center gap-3">
                  {getRoleIcon(member.role)}
                  <div>
                    <div className="font-medium text-slate-100">{member.full_name || member.email}</div>
                    <div className="text-sm text-slate-400 capitalize">{member.role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(member.status)}
                  {member.role !== 'admin' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeTeamMember(member.id)}
                      disabled={isLoading}
                      className="border-slate-600 bg-slate-600 hover:bg-slate-500 text-slate-200"
                    >
                      <UserX className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 